import { useMutation, useQuery } from "@tanstack/react-query";

import { GraphQLAPI } from "@examspark/api";
import { createWorkspaceShareSk } from "@examspark/common";
import {
  CreateWorkspaceShareInput,
  SearchableResourceFilterInput,
  SearchableWorkspaceShareConnection,
  WorkspaceShare,
} from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";

export const useQueryGetWorkspaceShare = (params: {
  sourceWorkspaceId: string;
  targetWorkspaceId: string;
  targetAppId?: string;
}) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["get-workspace-share", params],
    queryFn: async () => {
      const result = await GraphQLAPI.client.query<{
        getWorkspaceShare: WorkspaceShare;
      }>({
        query: /* GraphQL */ `
          query GetWorkspaceShare(
            $input: ModelGetInput!
            $authorizationContext: AuthorizationContextInput!
          ) {
            getWorkspaceShare(
              input: $input
              authorizationContext: $authorizationContext
            ) {
              id
              sk
              bankIds
              definition
              allowFilter
              workspaceId
            }
          }
        `,
        variables: {
          input: {
            id: params.sourceWorkspaceId,
            sk: createWorkspaceShareSk({
              workspaceId: params.targetWorkspaceId,
              appId: params.targetAppId,
            }),
          },
          authorizationContext,
        },
      });

      return result.data?.getWorkspaceShare;
    },
  });
};

export const useQuerySearchWorkspaceShares = (params: {
  publisherWorkspaceId?: string;
  consumerWorkspaceId?: string;
  consumerAppId?: string;
  filter?: string;
}) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["workspace-shares", params],
    enabled: !!params?.consumerWorkspaceId || !!params?.publisherWorkspaceId,
    queryFn: async () => {
      let filter: SearchableResourceFilterInput = {
        and: [{ sk: { wildcard: `workspace-share#*` } }],
      };

      if (params?.consumerWorkspaceId != null) {
        filter.and?.push({
          sk: {
            wildcard: `workspace-share*#${params.consumerWorkspaceId}/workspace`,
          },
        });
      }
      if (params?.consumerAppId != null) {
        filter.and?.push({
          sk: { wildcard: `workspace-share#${params.consumerAppId}/app*` },
        });
      }

      if (params?.publisherWorkspaceId != null) {
        filter.workspaceId = {
          eq: params?.publisherWorkspaceId,
        };
      }

      if (params?.filter) {
        filter = {
          and: [
            filter,
            {
              or: [
                { id: { wildcard: `*${params.filter}*` } },
                { sk: { wildcard: `*${params.filter}*` } },
              ],
            },
          ],
        };
      }

      const result = await GraphQLAPI.client.query<{
        searchWorkspaceShares: SearchableWorkspaceShareConnection;
      }>({
        query: /* GraphQL */ `
          query SearchWorkspaceShares(
            $filter: SearchableResourceFilterInput
            $sort: [SearchableResourceSortInput!]
            $limit: Int
            $nextToken: String
            $authorizationContext: AuthorizationContextInput
          ) {
            searchWorkspaceShares(
              filter: $filter
              sort: $sort
              limit: $limit
              nextToken: $nextToken
              authorizationContext: $authorizationContext
            ) {
              items {
                id
                sk
                workspaceId
                bankIds
                allowFilter
                consumer {
                  appId
                  appName
                  workspaceId
                  workspaceName
                }
                definition
              }
              nextToken
              total
              __typename
            }
          }
        `,
        variables: {
          filter,
          authorizationContext,
        },
      });

      return result.data?.searchWorkspaceShares;
    },
  });
};

export const useMutationCreateWorkspaceShare = () => {
  return useMutation({
    mutationFn: async (params: {
      workspaceId: string;
      targetWorkspaceId?: string;
      targetAppId?: string;
      allowFilter?: string;
      bankIds: CreateWorkspaceShareInput["bankIds"];
      definition?: string;
    }) => {
      await GraphQLAPI.client.query<{ createWorkspaceShare: WorkspaceShare }>({
        query: /* GraphQL */ `
          mutation CreateWorkspaceShare($input: CreateWorkspaceShareInput!) {
            createWorkspaceShare(input: $input) {
              id
              sk
            }
          }
        `,
        variables: {
          input: {
            id: params.workspaceId,
            sk: createWorkspaceShareSk({
              workspaceId: params.targetWorkspaceId,
              appId: params.targetAppId,
            }),
            schemaVersion: "2020-01-01",
            workspaceId: params.workspaceId,
            allowFilter: params.allowFilter,
            bankIds: params.bankIds,
            definition: params.definition,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          } as CreateWorkspaceShareInput,
        },
      });
    },
  });
};

export const useMutationUpdateWorkspaceShare = () => {
  return useMutation({
    mutationFn: async (params: {
      id: string;
      sk: string;
      workspaceId: string;
      bankIds?: string[];
      allowFilter?: string | null;
      definition?: string | null;
      productIds?: string[];
    }) => {
      await GraphQLAPI.client.query<{ updateWorkspaceShare: WorkspaceShare }>({
        query: /* GraphQL */ `
          mutation UpdateWorkspaceShare($input: UpdateWorkspaceShareInput!) {
            updateWorkspaceShare(input: $input) {
              id
              sk
            }
          }
        `,
        variables: {
          input: {
            ...params,
            schemaVersion: "2020-01-01",
          },
        },
      });
    },
  });
};

export const useMutationDeleteWorkspaceShare = () => {
  return useMutation({
    mutationFn: async (params: {
      originWorkspaceId: string;
      targetWorkspaceId: string;
    }) => {
      await GraphQLAPI.client.query<{ deleteWorkspaceShare: WorkspaceShare }>({
        query: /* GraphQL */ `
          mutation DeleteWorkspaceShare($input: DeleteWorkspaceShareInput!) {
            deleteWorkspaceShare(input: $input) {
              id
              sk
            }
          }
        `,
        variables: {
          input: {
            id: params.originWorkspaceId,
            sk: `workspace-share#${params.targetWorkspaceId}`,
            schemaVersion: "2020-01-01",
          },
        },
      });
    },
  });
};
